import { FC, useEffect, useRef } from "react";
import { Button, Form, Input, InputRef, Modal, message } from "antd";
import { RankingProjectService } from "modules/organization/services/rankingProject.service";
import { useRankingProjectListStore } from "modules/organization/store/rankingProjectListStore";

interface ICopyRankingProjectFormData {
  new_ranking_project_title: string;
}

const COPY_RANKING_PROJECT_TOAST_KEY = "copyRankingProjectKey";

const CopyRankingProject: FC<{
  open: boolean;
  projectSlug?: string;
  sourceRankingProjectId: number | null;
  sourceRankingProjectTitle: string;
  onClose: () => void;
}> = ({
  open,
  projectSlug,
  sourceRankingProjectId,
  sourceRankingProjectTitle,
  onClose,
}) => {
  const refreshRankingProjectList = useRankingProjectListStore(
    (state) => state.refreshRankingProjectList,
  );
  const [form] = Form.useForm<ICopyRankingProjectFormData>();
  const titleInputRef = useRef<InputRef | null>(null);

  const doCopyRankingProject = async (
    formValues: ICopyRankingProjectFormData,
  ) => {
    if (!projectSlug || !sourceRankingProjectId) {
      return;
    }
    onClose();
    message.open({
      key: COPY_RANKING_PROJECT_TOAST_KEY,
      content: "Copying ranking project...",
      type: "loading",
      duration: 0,
    });
    try {
      await new RankingProjectService().copyRankingProject(
        projectSlug,
        sourceRankingProjectId,
        formValues.new_ranking_project_title,
      );
      await refreshRankingProjectList(projectSlug);
      message.success("Ranking project copied successfully.");
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ??
          "Failed to copy ranking project.",
      );
    } finally {
      message.destroy(COPY_RANKING_PROJECT_TOAST_KEY);
    }
  };

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        new_ranking_project_title: `${sourceRankingProjectTitle.slice(0, 190)} (Copy)`,
      });
      titleInputRef.current?.focus();
    }
  }, [open, form, sourceRankingProjectTitle]);

  return (
    <Modal
      className="confirmationModal"
      wrapClassName="addModal"
      title={<h2>Copy Ranking Project</h2>}
      centered
      open={open}
      onCancel={onClose}
      footer={[
        <Button htmlType="button" key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          type="primary"
          onClick={() => form.submit()}
          htmlType="button"
          key="copy"
        >
          Copy
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => {
          doCopyRankingProject(values).catch(console.error);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            form.submit();
          }
        }}
      >
        <Form.Item
          name="new_ranking_project_title"
          label="New Ranking Project title"
          rules={[
            { required: true, message: "Title must not be empty" },
            { whitespace: true, message: "Title must not be empty" },
          ]}
        >
          <Input maxLength={200} ref={titleInputRef} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CopyRankingProject;
